import React from "react";

import { PencilSimple, TrashSimple } from "@magnetic/icons";

import { Button, EditButton } from "src/components/buttons";
import { HFlex } from "src/components/hFlex";

import { BaseColumnConfig, Config } from "./baseColumnConfig";

export type ActionCallback = () => void;

export type EditDeleteActions = [
  ActionCallback | undefined,
  ActionCallback | undefined
];

/**
 * Common rules for actions columns
 * Changes configuration object in place
 */
function initializeColumnConfiguration(config: Config): void {
  config.rowIdentifier ??= false; // action buttons are not a good contender for key participation
  config.label ??= ""; // Action columns tend not to have a label
  config.pinned ??= "right"; // UX standard, actions are pinned on the right
  config.sortable ??= false; // actions are not sortable

  config.agGridColumnDefinition ??= {};
  config.agGridColumnDefinition.suppressAutoSize ??= true;
  config.agGridColumnDefinition.sortable ??= false;
  config.agGridColumnDefinition.resizable ??= false;
  config.agGridColumnDefinition.autoHeight ??= false;
  config.agGridColumnDefinition.flex ??= 0;
  // constrain the action column to a practical initial/minimum width
  // since the action column will be alone in the right pinned column
  if (config.size) {
    config.agGridColumnDefinition.initialWidth ??= config.size;
    config.agGridColumnDefinition.minWidth ??= config.size;
  }
}

/**
 * ActionColumn for candidates has morphed into the edit
 * actions column. This will be to support candidates running
 * vs edit mode. If you need non mode related actions please
 * use the RowActionsColumn.
 */
export class ActionColumn extends BaseColumnConfig<EditDeleteActions> {
  private readonly ignoreSoftwareMode: boolean;

  constructor(config: Config & { readonly ignoreSoftwareMode?: boolean }) {
    initializeColumnConfiguration(config);

    super(config);
    this.ignoreSoftwareMode = config.ignoreSoftwareMode ?? false;
  }

  identity() {
    return undefined; // action column does not participate as a row identifier
  }

  compare(): number {
    return 0;
  }

  render(value: EditDeleteActions) {
    const [editCb, deleteCb] = value;
    return (
      <HFlex gap="none">
        {editCb && (
          <EditButton
            icon={<PencilSimple size={16} weight="bold" />}
            kind="tertiary"
            onClick={editCb}
            name="edit"
            data-qa="edit"
            ignoreSoftwareMode={this.ignoreSoftwareMode}
          />
        )}
        {deleteCb && (
          <EditButton
            icon={<TrashSimple size={16} weight="bold" />}
            kind="tertiary"
            onClick={deleteCb}
            name="delete"
            data-qa="delete"
            ignoreSoftwareMode={this.ignoreSoftwareMode}
          />
        )}
      </HFlex>
    );
  }
}

/**
 * Optional state passed along with RowAction.
 * Usage is for manual cell-level change detection needs from AG Grid. Avoid constant flashing of the button cell
 */
export interface RowActionContext {}

type RowActionIcon<CTX extends RowActionContext = object> = {
  customComponent?: React.ReactElement;
  icon?: React.ReactElement;
  callback?: ActionCallback;
  context?: CTX;
};

export type RowActions<CTX extends RowActionContext = object> =
  RowActionIcon<CTX>[];

/**
 * This column is non-candidate based.
 * it will not hide/show actions based
 * on the mode.
 */
export class RowActionsColumn<
  CTX extends RowActionContext
> extends BaseColumnConfig<RowActions<CTX>> {
  constructor(config: Config) {
    initializeColumnConfiguration(config);
    super(config);
  }

  identity() {
    return undefined; // action column does not participate as a row identifier
  }

  compare(): number {
    return 0;
  }

  render(icons: RowActions<CTX>) {
    const buttons = icons.map(
      (
        { icon, callback, customComponent }: RowActionIcon<CTX>,
        index: number
      ) => {
        if (customComponent) {
          return customComponent;
        }
        return (
          <Button
            name={`action-column-button-${index.toString()}`}
            key={`button-${index.toString()}`}
            icon={icon}
            kind="tertiary"
            onClick={callback}
          />
        );
      }
    );
    return <HFlex gap="none">{buttons}</HFlex>;
  }
}
